import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { SEO, seoConfig } from "@/hooks/useSEO";
import { generateWebPageSchema, generateBreadcrumbSchema, generateArticleSchema } from "@/lib/jsonld";

// Images
import coverImage from "@/assets/blog/fidelisation-client-guide/couverture.webp";
import acquisitionImage from "@/assets/blog/fidelisation-client-guide/acquisition-vs-fidelisation-cout-client.webp";
import piliersImage from "@/assets/blog/fidelisation-client-guide/piliers-fidelisation-client-durable.webp";
import indicateursImage from "@/assets/blog/fidelisation-client-guide/indicateurs-fidelisation-retention-reachat-frequence.webp";
import evolutionImage from "@/assets/blog/fidelisation-client-guide/evolution-outils-fidelisation-avant-2020-2026.webp";

const FidelisationClientGuide = () => {
  const jsonLdSchemas = [
    generateWebPageSchema(
      "Comment fidéliser ses clients : le guide complet 2026",
      seoConfig.blogFidelisationClientGuide.description,
      "/fidelisation-client-guide"
    ),
    generateBreadcrumbSchema([
      { name: "Accueil", path: "/" },
      { name: "Blog", path: "/blog" },
      { name: "Fidélisation client guide", path: "/fidelisation-client-guide" }
    ]),
    generateArticleSchema(
      "Comment fidéliser ses clients : le guide complet 2026",
      seoConfig.blogFidelisationClientGuide.description,
      "https://unotif.com/fidelisation-client-guide",
      "2026-03-31"
    )
  ];

  const imageStyle = {
    boxShadow: "0 8px 32px -8px rgba(0,0,0,0.12), 0 4px 16px -4px rgba(0,0,0,0.08)",
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO {...seoConfig.blogFidelisationClientGuide} jsonLd={jsonLdSchemas} />
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="pt-24 lg:pt-40 pb-12 sm:pb-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Breadcrumb */}
            <nav className="text-sm font-medium text-[#495456] mb-6">
              <Link to="/blog" className="text-[#0c97a9] hover:underline">Blog</Link>
              <span className="mx-2">›</span>
              <span className="text-[#074155]">Fidélisation client guide</span>
            </nav>

            {/* H1 */}
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155] mb-8">
              Comment fidéliser ses clients : le guide complet 2026
            </h1>

            {/* Cover Image */}
            <img
              src={coverImage}
              alt="Illustration de la fidélisation client avec stratégies et outils pour améliorer la rétention et la relation client en 2026"
              loading="eager"
              className="rounded-2xl w-full h-auto"
              style={imageStyle}
            />
          </div>
        </section>

        {/* Article Content */}
        <section className="pb-20 sm:pb-28 lg:pb-36">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">

            {/* Introduction */}
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">Fidéliser ses clients</strong> n'est pas une option réservée aux grandes enseignes. C'est la stratégie la plus rentable accessible à n'importe quelle structure, quelle que soit sa taille. Et pourtant, la plupart des commerces et des prestataires passent la majorité de leur énergie à acquérir de nouveaux clients plutôt qu'à garder ceux qu'ils ont déjà.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le chiffre qui devrait changer cette priorité : 68 % des clients qui quittent une entreprise le font non pas à cause d'un problème de produit ou de prix, mais parce qu'ils se sentaient négligés. Ils n'ont pas pris la peine de se plaindre. Ils sont partis en silence. Ce guide couvre les fondamentaux de la <strong className="font-semibold">fidélisation client</strong>, les 4 grandes stratégies à déployer, et les premières actions concrètes à mettre en place pour inverser cette tendance.
            </p>

            {/* H2 - Définition et enjeux */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Fidélisation client : définition et enjeux réels en 2026
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La <strong className="font-semibold">fidélisation client</strong> désigne l'ensemble des actions qui incitent un client à revenir, à dépenser davantage et à recommander votre offre à son entourage. Ce n'est pas un programme de points. C'est un système de relation.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un client fidèle dépense en moyenne 67 % de plus qu'un nouveau client (Bain & Company). Il est aussi moins sensible aux prix et plus indulgent face à une erreur occasionnelle. La loi de Pareto s'applique dans presque tous les secteurs : 20 % de vos clients génèrent 50 à 80 % de votre chiffre d'affaires. Identifier ces clients, les traiter différemment et les garder longtemps : c'est là que se joue la rentabilité d'une activité.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La distinction fondamentale est celle-ci : la satisfaction est une émotion ponctuelle. La <strong className="font-semibold">fidélité client</strong> est un comportement durable. Un client peut repartir très satisfait d'une première visite et ne jamais revenir si personne ne crée de raison concrète de le faire.
            </p>

            <img
              src={acquisitionImage}
              alt="Infographie comparant coût acquisition client et fidélisation avec avantage économique de garder ses clients"
              loading="lazy"
              className="rounded-2xl w-full h-auto my-8"
              style={imageStyle}
            />

            {/* H2 - Client satisfait */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Pourquoi un client satisfait n'est pas forcément un client fidèle
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              C'est l'idée reçue la plus répandue et la plus coûteuse : "mes clients sont contents, ils reviendront." Les données contredisent cette conviction de façon systématique.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              68 % des clients qui quittent une entreprise se déclaraient satisfaits lors de leur dernier contact. La satisfaction mesure un instant. La fidélité mesure une durée. Entre les deux, il y a un vide que peu de structures comblent activement.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les 3 raisons les plus fréquentes qui font partir un client satisfait en silence : le sentiment de ne pas être reconnu malgré une fréquentation régulière, l'absence de contact entre deux achats, et une offre concurrente plus personnalisée qui capte l'attention laissée vacante.
            </p>

            {/* H2 - 4 piliers */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Les 4 piliers d'une stratégie de fidélisation solide
            </h2>

            <img
              src={piliersImage}
              alt="Schéma des 4 piliers de la fidélisation client avec connaissance, personnalisation, animation et valeur ajoutée"
              loading="lazy"
              className="rounded-2xl w-full h-auto my-8"
              style={imageStyle}
            />

            <h3 className="text-lg sm:text-xl font-bold text-[#022226] mb-3 mt-8">
              1. La connaissance client
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Vous ne pouvez pas personnaliser ce que vous ne connaissez pas. La <strong className="font-semibold">connaissance client</strong> commence par quelques données simples : fréquence de visite, panier moyen, historique des achats. Pas besoin d'un CRM à 300 euros par mois pour démarrer : un tableur bien tenu suffit dans un premier temps.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              L'objectif minimal : identifier vos 20 % de clients qui génèrent 80 % de votre chiffre d'affaires. Ces clients méritent une attention différenciée, une communication spécifique, et une mécanique de récompense adaptée à leur valeur.
            </p>

            <h3 className="text-lg sm:text-xl font-bold text-[#022226] mb-3 mt-8">
              2. La personnalisation de la relation
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un message générique envoyé à tous vos clients n'est pas une relation : c'est une diffusion. La <strong className="font-semibold">personnalisation</strong> ne demande pas de technologie sophistiquée pour commencer. Deux segments suffisent : clients réguliers et clients occasionnels. Un message différent pour chacun produit déjà des résultats mesurables.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les exemples les plus simples : un message d'anniversaire automatique avec le prénom du client, une notification qui fait référence à son dernier achat, une offre construite sur sa fréquence de visite habituelle. Ce niveau de personnalisation crée un sentiment de reconnaissance que le client perçoit immédiatement.
            </p>

            <h3 className="text-lg sm:text-xl font-bold text-[#022226] mb-3 mt-8">
              3. L'animation régulière
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un programme de <strong className="font-semibold">fidélisation</strong> sans animation est un programme silencieux. La majorité des clients qui s'inscrivent à un programme de fidélité n'y repensent plus après 3 semaines si personne ne les contacte.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le minimum viable : 1 point de contact par mois. Les canaux par ordre d'efficacité en 2026 : la notification push wallet (53 % de taux d'ouverture moyen), le SMS (90 % mais coût par message), et l'email (23 à 30 % d'ouverture). Le principe du calendrier d'animation : planifier 4 temps forts par trimestre (offre flash, événement exclusif membres, message d'anniversaire, réactivation des inactifs).
            </p>

            <h3 className="text-lg sm:text-xl font-bold text-[#022226] mb-3 mt-8">
              4. La création de valeur au-delà du produit
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Récompenser les achats est bien. Créer un sentiment d'appartenance est plus durable. Les clients qui se sentent membres d'une communauté restent plus longtemps et recommandent plus activement que ceux qui cumulent simplement des points.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Exemples concrets : un accès anticipé aux nouveautés réservé aux membres, un contenu exclusif envoyé uniquement à votre base, un événement privé ouvert seulement aux clients fidèles.
            </p>

            {/* H2 - Deux types */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Les deux types de fidélisation client
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La <strong className="font-semibold">fidélisation transactionnelle</strong> fonctionne sur les récompenses : points, tampons, cashback, remises. Elle est rapide à mettre en place, facile à mesurer et produit des résultats visibles sur la fréquence de visite à court terme. Son point faible : la fidélité dure aussi longtemps que la récompense. Si un concurrent propose mieux, le client part.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La <strong className="font-semibold">fidélisation émotionnelle</strong> fonctionne sur l'attachement : sentiment d'appartenance, reconnaissance individuelle, confiance construite dans la durée. Elle est plus longue à développer mais beaucoup plus résistante à la concurrence. Un client émotionnellement attaché ne quitte pas pour une remise de 10 % chez un concurrent.
            </p>

            {/* Tableau comparatif */}
            <div className="overflow-x-auto my-8">
              <table className="w-full text-left text-[14px] sm:text-base font-medium text-[#495456] border-collapse">
                <thead>
                  <tr className="border-b-2 border-[#074155]">
                    <th className="py-3 pr-4 text-[#074155] font-semibold">Critère</th>
                    <th className="py-3 pr-4 text-[#074155] font-semibold">Transactionnelle</th>
                    <th className="py-3 text-[#074155] font-semibold">Émotionnelle</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 pr-4 font-medium">Mise en place</td>
                    <td className="py-3 pr-4 font-medium">Rapide (1 semaine)</td>
                    <td className="py-3 font-medium">Progressive (3 à 6 mois)</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 pr-4 font-medium">Durabilité</td>
                    <td className="py-3 pr-4 font-medium">Faible à moyenne</td>
                    <td className="py-3 font-medium">Élevée</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 pr-4 font-medium">Résistance au concurrent</td>
                    <td className="py-3 pr-4 font-medium">Faible</td>
                    <td className="py-3 font-medium">Élevée</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 pr-4 font-medium">Coût</td>
                    <td className="py-3 pr-4 font-medium">Moyen (récompenses)</td>
                    <td className="py-3 font-medium">Faible à moyen</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les deux approches se combinent. Le programme transactionnel crée le prétexte au contact et la mécanique de retour. L'approche émotionnelle crée l'attachement qui rend le programme indispensable au-delà des récompenses.
            </p>

            {/* H2 - Par où commencer */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Par où commencer quand on n'a jamais fidélisé ses clients
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le premier réflexe est souvent de chercher l'outil parfait avant d'avoir les bases. C'est le chemin le plus efficace pour ne pas démarrer. Voici un plan en 3 étapes, applicables en une semaine.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">Étape 1 : créer un canal de contact direct.</strong> Sans canal de contact, chaque client qui passe repart dans le vide. Trois options accessibles : l'email (formulaire d'inscription en caisse ou en ligne), le SMS (opt-in obligatoire), ou le wallet mobile (un QR code scanné en 30 secondes, sans téléchargement). Le wallet se distingue par sa friction quasi nulle : Apple Wallet et Google Wallet sont déjà installés sur tous les smartphones, et le taux d'adoption est 3 à 5 fois supérieur à celui d'une application dédiée.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">Étape 2 : mettre en place une récompense atteignable en 3 à 5 visites.</strong> Une première récompense trop lointaine décourage et génère de l'abandon. La règle est simple : le client doit voir la récompense accessible rapidement pour rester engagé.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">Étape 3 : planifier 1 communication par mois minimum.</strong> La planification à l'avance est ce qui distingue les programmes qui tiennent dans la durée de ceux qui s'éteignent après 6 semaines. Un calendrier de 90 jours posé une fois suffit pour sécuriser la continuité.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les premiers résultats se voient entre 60 et 90 jours.
            </p>

            {/* H2 - Mesurer */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Comment mesurer si votre stratégie de fidélisation fonctionne
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un programme sans mesure est un programme aveugle. Trois indicateurs suffisent pour commencer.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le <strong className="font-semibold">taux de rétention</strong> mesure le pourcentage de clients qui reviennent d'une période à l'autre. Un taux de rétention en hausse de 5 % génère entre 25 et 95 % de bénéfices supplémentaires selon le secteur (Bain & Company). Le <strong className="font-semibold">taux de réachat</strong> mesure la proportion de clients qui ont effectué au moins deux achats sur une période donnée. La <strong className="font-semibold">fréquence de visite</strong> mesure le nombre moyen de visites par client actif sur 90 jours.
            </p>

            <img
              src={indicateursImage}
              alt="Tableau de bord avec taux de rétention, taux de réachat et fréquence de visite pour suivre la fidélisation client"
              loading="lazy"
              className="rounded-2xl w-full h-auto my-8"
              style={imageStyle}
            />

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Ces 3 chiffres, suivis mensuellement dans un tableur simple, donnent une vision claire de l'évolution de votre programme.
            </p>

            {/* H2 - Ambassadeur */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              De la fidélisation à l'ambassadeur : l'étape suivante
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un client fidèle est un actif silencieux. Un client ambassadeur est un actif actif : il parle de vous, il recommande, il ramène de nouveaux clients sans coût d'acquisition supplémentaire.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le NPS (Net Promoter Score) mesure exactement cet écart. Les clients qui notent votre offre 9 ou 10 sur 10 génèrent un bouche-à-oreille actif. Ceux qui la notent 7 ou 8 sont des clients fidèles mais passifs. L'objectif est de créer les conditions qui font passer un client de "content de revenir" à "incapable de ne pas en parler autour de lui".
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Ces conditions ne tiennent pas à la qualité du produit. Elles tiennent à l'expérience vécue : la reconnaissance individuelle, le sentiment d'appartenance, et une occasion simple de partager.
            </p>

            {/* H2 - Ce qui a changé en 2026 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              La fidélisation client en 2026 : ce qui a changé
            </h2>

            <img
              src={evolutionImage}
              alt="Comparaison des outils de fidélisation avant 2020 et en 2026 avec passage au wallet mobile et notifications personnalisées"
              loading="lazy"
              className="rounded-2xl w-full h-auto my-8"
              style={imageStyle}
            />

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le canal déterminant en 2026 n'est pas l'email ou les réseaux sociaux. C'est le smartphone. Plus précisément : la capacité à envoyer un message directement sur l'écran verrouillé du téléphone, sans dépendre d'une boîte mail saturée et sans être soumis à un algorithme de réseau social.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le <strong className="font-semibold">wallet marketing</strong> répond exactement à ce besoin. Une carte de <strong className="font-semibold">fidélité numérique</strong> ajoutée dans Apple Wallet ou Google Wallet permet d'envoyer des notifications push directement au client, avec un taux d'ouverture de 53 % en moyenne : deux fois supérieur à celui de l'email. Le canal est direct, personnel, et le message arrive au moment choisi, pas quand l'algorithme le décide.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">Fidéliser ses clients</strong> en 2026, c'est construire un actif d'audience qui vous appartient. Pas dépendant d'une plateforme externe, pas soumis aux variations d'algorithme, pas effaçable par une mise à jour des règles d'un réseau social.
            </p>

            {/* Conclusion */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Conclusion
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La <strong className="font-semibold">fidélisation client</strong> n'est pas un projet à lancer "un jour". C'est un système à construire progressivement : un canal de contact, une mécanique de récompense, une animation régulière, une mesure mensuelle. Les structures qui maîtrisent ces 4 éléments voient leur chiffre d'affaires progresser sans augmenter leur budget acquisition. Créez votre première carte wallet sur Unotif et commencez à contacter vos membres dès aujourd'hui.
            </p>

            {/* Section maillage interne — encart enrichi pilier */}
            <div className="mt-16 rounded-2xl border border-border bg-[hsl(var(--unotif-50))] p-6 sm:p-8 shadow-sm">
              <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4">
                Approfondir votre stratégie de fidélisation
              </h2>
              <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-6">
                La fidélisation ne repose jamais sur un seul levier. Pour aller plus loin et structurer une approche vraiment efficace, certains points méritent d'être creusés en détail.
              </p>

              <div className="grid gap-4 sm:grid-cols-2">
                <Link
                  to="/pourquoi-clients-partent"
                  className="group flex items-start gap-3 rounded-xl bg-white p-4 shadow-sm border border-border transition-shadow hover:shadow-md"
                >
                  <span className="mt-0.5 text-lg shrink-0">🔍</span>
                  <div>
                    <span className="text-[14px] sm:text-[15px] font-semibold text-[#074155] group-hover:text-[#0c97a9] transition-colors">
                      Pourquoi 68 % des clients partent en silence
                    </span>
                    <p className="text-[13px] sm:text-sm text-[#495456] mt-1 leading-snug">
                      Comprenez les vrais signaux de départ et les leviers pour les retenir avant qu'il ne soit trop tard.
                    </p>
                  </div>
                </Link>

                <Link
                  to="/fidelisation-emotionnelle-vs-transactionnelle"
                  className="group flex items-start gap-3 rounded-xl bg-white p-4 shadow-sm border border-border transition-shadow hover:shadow-md"
                >
                  <span className="mt-0.5 text-lg shrink-0">💛</span>
                  <div>
                    <span className="text-[14px] sm:text-[15px] font-semibold text-[#074155] group-hover:text-[#0c97a9] transition-colors">
                      Fidélisation émotionnelle vs transactionnelle
                    </span>
                    <p className="text-[13px] sm:text-sm text-[#495456] mt-1 leading-snug">
                      Pourquoi l'attachement dure quand la récompense seule ne suffit pas à retenir vos clients.
                    </p>
                  </div>
                </Link>

                <Link
                  to="/client-ambassadeur"
                  className="group flex items-start gap-3 rounded-xl bg-white p-4 shadow-sm border border-border transition-shadow hover:shadow-md"
                >
                  <span className="mt-0.5 text-lg shrink-0">📣</span>
                  <div>
                    <span className="text-[14px] sm:text-[15px] font-semibold text-[#074155] group-hover:text-[#0c97a9] transition-colors">
                      Transformer un client satisfait en ambassadeur
                    </span>
                    <p className="text-[13px] sm:text-sm text-[#495456] mt-1 leading-snug">
                      Les conditions concrètes pour activer la recommandation spontanée et déclencher un vrai bouche-à-oreille.
                    </p>
                  </div>
                </Link>

                <Link
                  to="/7-actions-fidelisation-client"
                  className="group flex items-start gap-3 rounded-xl bg-white p-4 shadow-sm border border-border transition-shadow hover:shadow-md"
                >
                  <span className="mt-0.5 text-lg shrink-0">⚡</span>
                  <div>
                    <span className="text-[14px] sm:text-[15px] font-semibold text-[#074155] group-hover:text-[#0c97a9] transition-colors">
                      7 actions de fidélisation à mettre en place dès cette semaine
                    </span>
                    <p className="text-[13px] sm:text-sm text-[#495456] mt-1 leading-snug">
                      Un plan étape par étape, actionnable immédiatement, sans compétences techniques avancées.
                    </p>
                  </div>
                </Link>
              </div>
            </div>

          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default FidelisationClientGuide;
